import { LSPAYErrorMessage } from '../../helpers/lspay.types'
import { LSPAY_ERROR, LSPAY_ERROR_MESSAGES } from '../../helpers/lspayConstants'

// Polling configuration
const POLLING_MAX_DURATION_MS = 60000 // 60 seconds
const POLLING_INITIAL_DELAY_MS = 1000 // 1 second
const POLLING_BACKOFF_MULTIPLIER = 1.5

// this makes testing easier
export interface TimerInterface {
  now: () => number
  setTimeout: (callback: () => void, delay: number) => NodeJS.Timeout
}
// this makes testing easier
export const defaultTimer: TimerInterface = {
  now: () => Date.now(),
  setTimeout: (callback: () => void, delay: number) =>
    setTimeout(callback, delay),
}

// Polling context type
export interface PollingContext {
  startTime: number
  currentDelay: number
  attemptCount: number
}

// Polling configuration type
export interface PollingConfig {
  maxDurationMs: number
  initialDelayMs: number
  backoffMultiplier: number
}

// Work function type - can be any async function that returns a result
export type WorkFunction<T> = () => Promise<T>

// Work result type - must have a success indicator
export interface WorkResult<T> {
  isSuccess: boolean
  data?: T
  error?: string
}

export const defaultPollingConfig: PollingConfig = {
  maxDurationMs: POLLING_MAX_DURATION_MS,
  initialDelayMs: POLLING_INITIAL_DELAY_MS,
  backoffMultiplier: POLLING_BACKOFF_MULTIPLIER,
}

export const createGenericPoller = <T>(
  workFunction: WorkFunction<WorkResult<T>>,
  timer: TimerInterface = defaultTimer,
  config: PollingConfig = defaultPollingConfig
) => {
  return async (
    context: PollingContext,
    onSuccess: (data: T) => void,
    onTimeout: () => void,
    onError: (errorMessage: LSPAYErrorMessage) => void,
    onRetry: (newContext: PollingContext) => void
  ): Promise<void> => {
    try {
      const result = await workFunction()

      if (result.isSuccess && result.data) {
        onSuccess(result.data)
        return
      }

      // Check if we've exceeded the maximum polling duration
      const elapsedTime = timer.now() - context.startTime
      if (elapsedTime >= config.maxDurationMs) {
        onTimeout()
        return
      }

      // Calculate next delay with exponential backoff
      const nextDelay = Math.min(
        context.currentDelay * config.backoffMultiplier,
        config.maxDurationMs / 4
      )

      // Schedule next poll
      timer.setTimeout(() => {
        onRetry({
          startTime: context.startTime,
          currentDelay: nextDelay,
          attemptCount: context.attemptCount + 1,
        })
      }, context.currentDelay)
    } catch (_error) {
      onError(LSPAY_ERROR_MESSAGES[LSPAY_ERROR.DEFAULT_PAYMENT])
    }
  }
}
